import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { X, MapPin, ExternalLink, Search, ChevronLeft, ChevronRight, Filter, Loader } from 'react-feather';
import { getCamps, getCampFilters, Camp, CampFilters, CampFilterOptions } from '../services/campsAPI';
import Modal from './Modal';
import { Select } from './ui';

interface CampsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const US_STATES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'Washington DC'
};

const CampsModal: React.FC<CampsModalProps> = ({ isOpen, onClose }) => {
  const { sessionToken } = useAuth();
  const { isMobile } = useResponsive();

  const [camps, setCamps] = useState<Camp[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filterOptions, setFilterOptions] = useState<CampFilterOptions | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<CampFilters>({
    state: '',
    activityType: '',
    eventType: '',
    gender: '',
    nearMe: false,
    limit: 12
  });

  const fetchCamps = useCallback(async () => {
    if (!sessionToken) return;
    setLoading(true);
    setLoadError(false);
    try {
      const activeFilters: CampFilters = { ...filters, page };
      // Remove empty string filters
      Object.keys(activeFilters).forEach(key => {
        const k = key as keyof CampFilters;
        if (activeFilters[k] === '' || activeFilters[k] === undefined) {
          delete activeFilters[k];
        }
      });
      const data = await getCamps(sessionToken, activeFilters);
      setCamps(data.camps);
      setTotalPages(data.pagination.totalPages);
      setTotal(data.pagination.total);
    } catch (error) {
      console.error('Error fetching camps:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, filters, page]);

  const fetchFilters = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const data = await getCampFilters(sessionToken);
      setFilterOptions(data);
    } catch (error) {
      console.error('Error fetching camp filter options:', error);
    }
  }, [sessionToken]);

  // Filter options only need to load once per open.
  useEffect(() => {
    if (isOpen) fetchFilters();
  }, [isOpen, fetchFilters]);

  // A filter change resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // Debounced camps fetch (audit #246): a single filter change updates both
  // `filters` and (via the effect above) `page`, which previously fired TWO
  // fetches — one with the stale page, one after the reset — causing a flicker.
  // The debounce collapses those back-to-back dependency changes into one request.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => { fetchCamps(); }, 150);
    return () => clearTimeout(t);
  }, [isOpen, fetchCamps]);

  if (!isOpen) return null;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatLocation = (camp: Camp) => {
    const parts = [camp.location_city, camp.location_state].filter(Boolean);
    return parts.join(', ') || 'Location TBD';
  };

  const handleRegister = (url: string | null) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const resetFilters = () => {
    setFilters({ state: '', activityType: '', eventType: '', gender: '', nearMe: false, limit: 12 });
  };

  const activeFilterCount = [filters.state, filters.activityType, filters.eventType, filters.gender].filter(Boolean).length + (filters.nearMe ? 1 : 0);

  return (
    <>
      {/* Shared Modal wrapper (audit #223): dialog semantics, Escape, focus trap, scroll lock */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        labelledById="camps-modal-title"
        zIndex={9998}
        contentStyle={{
          background: '#f8f9fa',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '1200px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Manrope, sans-serif',
        }}
      >
          {/* Header */}
          <div style={{
            padding: isMobile ? '16px' : '20px 28px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#fff',
            flexShrink: 0
          }}>
            <div>
              <h2 id="camps-modal-title" style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#1F2937' }}>
                Camps
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>
                {loading ? 'Loading...' : `${total} upcoming camp${total !== 1 ? 's' : ''}`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Filter toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  border: activeFilterCount > 0 ? '2px solid #F9550D' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: activeFilterCount > 0 ? '#FFF5F0' : '#fff',
                  color: activeFilterCount > 0 ? '#F9550D' : '#374151',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                <Filter size={14} />
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </button>
              {/* Near Me toggle */}
              <button
                onClick={() => setFilters(f => ({ ...f, nearMe: !f.nearMe }))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  border: filters.nearMe ? '2px solid #F9550D' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: filters.nearMe ? '#F9550D' : '#fff',
                  color: filters.nearMe ? '#fff' : '#374151',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                <MapPin size={14} />
                Near Me
              </button>
              {/* Close */}
              <button
                onClick={onClose}
                aria-label="Close camps"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={22} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Filter Bar (collapsible) */}
          {showFilters && (
            <div style={{
              padding: isMobile ? '12px 16px' : '12px 28px',
              borderBottom: '1px solid #e9ecef',
              background: '#fff',
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
              flexShrink: 0
            }}>
              {/* Four filters, no visible labels, so each names itself for a
                  screen reader. <Select> draws its own chevron, so the
                  `aps-chevron` class is gone — it would paint a second one.

                  Each wrapping <div> is load-bearing: <Select> hangs its
                  chevron on a width:100% span, and as a direct child of this
                  flex row that span would claim the whole line. Inside a plain
                  div the four stay content-sized, as the bare selects were. */}
              <div>
                <Select
                  aria-label="Filter by state"
                  value={filters.state || ''}
                  onChange={(e) => setFilters(f => ({ ...f, state: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="">All States</option>
                  {(filterOptions?.states || []).map(s => (
                    <option key={s} value={s}>{US_STATES[s] || s}</option>
                  ))}
                </Select>
              </div>

              <div>
                <Select
                  aria-label="Filter by sport"
                  value={filters.activityType || ''}
                  onChange={(e) => setFilters(f => ({ ...f, activityType: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="">All Sports</option>
                  {(filterOptions?.activityTypes || []).map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </Select>
              </div>

              <div>
                <Select
                  aria-label="Filter by event type"
                  value={filters.eventType || ''}
                  onChange={(e) => setFilters(f => ({ ...f, eventType: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="">All Types</option>
                  {(filterOptions?.eventTypes || []).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </div>

              <div>
                <Select
                  aria-label="Filter by gender"
                  value={filters.gender || ''}
                  onChange={(e) => setFilters(f => ({ ...f, gender: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="">All Genders</option>
                  {(filterOptions?.genders || []).map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </Select>
              </div>

              {activeFilterCount > 0 && (
                <button
                  onClick={resetFilters}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#fee2e2',
                    color: '#991b1b',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
          )}

          {/* Camp List */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: isMobile ? '12px' : '20px 28px'
          }}>
            {loading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 0',
                color: '#6B7280'
              }}>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} />
                Loading camps...
              </div>
            ) : loadError ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#6B7280'
              }}>
                <p style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 4px' }}>Couldn't load camps</p>
                <p style={{ fontSize: '13px', margin: '0 0 16px' }}>Something went wrong while loading camps.</p>
                <button
                  onClick={() => fetchCamps()}
                  style={{ padding: '8px 18px', background: '#f9550d', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Retry
                </button>
              </div>
            ) : camps.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#6B7280'
              }}>
                <MapPin size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 4px' }}>No camps found</p>
                <p style={{ fontSize: '13px', margin: 0 }}>
                  {activeFilterCount > 0 ? 'Try adjusting your filters' : 'Check back later for upcoming camps'}
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '14px'
              }}>
                {camps.map((camp, idx) => (
                  <CampCard
                    key={`${camp.event_id}-${idx}`}
                    camp={camp}
                    onRegister={handleRegister}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              padding: '12px 28px',
              borderTop: '1px solid #e9ecef',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              flexShrink: 0
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={paginationBtnStyle(page <= 1)}
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={paginationBtnStyle(page >= totalPages)}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
      </Modal>

      {/* Spin animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

/* ----------- Camp Card Sub-Component ----------- */

interface CampCardProps {
  camp: Camp;
  onRegister: (url: string | null) => void;
  isMobile: boolean;
}

const CampCard: React.FC<CampCardProps> = ({ camp, onRegister, isMobile }) => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const location = [camp.location_city, camp.location_state].filter(Boolean).join(', ') || 'Location TBD';
  const dateDisplay = camp.date_range || `${formatDate(camp.start_date)}${camp.end_date && camp.end_date !== camp.start_date ? ' - ' + formatDate(camp.end_date) : ''}`;

  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'box-shadow 0.2s ease, transform 0.2s ease'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Card Header with logo */}
      <div style={{
        padding: '16px 16px 12px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start'
      }}>
        {camp.logo_url ? (
          <img
            src={camp.logo_url}
            alt=""
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '8px',
              objectFit: 'cover',
              flexShrink: 0,
              background: '#f3f4f6'
            }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '8px',
            background: '#FFF5F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <MapPin size={20} color="#F9550D" />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: '700',
            color: '#1F2937',
            lineHeight: '1.3',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {camp.event_name}
          </h3>
          {camp.organizer && (
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9CA3AF', fontWeight: '500' }}>
              {camp.organizer}
            </p>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div style={{ padding: '0 16px 12px', flex: 1 }}>
        {/* Date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>{dateDisplay}</span>
        </div>

        {/* Location */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <MapPin size={13} color="#6B7280" />
          <span style={{ fontSize: '12px', color: '#6B7280' }}>{location}</span>
          {camp.distance_miles != null && (
            <span style={{
              fontSize: '11px',
              color: '#F9550D',
              fontWeight: '600',
              marginLeft: '4px'
            }}>
              {Math.round(camp.distance_miles)} mi
            </span>
          )}
        </div>

        {/* Tags row */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {camp.event_type && (
            <span style={tagStyle('#EFF6FF', '#1E40AF')}>{camp.event_type}</span>
          )}
          {camp.activity_type && (
            <span style={tagStyle('#F0FDF4', '#166534')}>{camp.activity_type}</span>
          )}
          {camp.gender && (
            <span style={tagStyle('#F5F3FF', '#5B21B6')}>{camp.gender}</span>
          )}
          {camp.cost_display && (
            <span style={tagStyle('#FFF7ED', '#9A3412')}>{camp.cost_display}</span>
          )}
        </div>

        {/* Grade/Age info */}
        {(camp.grade_range || camp.ages) && (
          <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
            {[camp.grade_range, camp.ages].filter(Boolean).join(' | ')}
          </p>
        )}
      </div>

      {/* Card Footer */}
      <div style={{
        padding: '10px 16px',
        borderTop: '1px solid #F3F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
      }}>
        {/* Status badges */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {camp.is_sold_out && (
            <span style={badgeStyle('#FEE2E2', '#991B1B')}>Sold Out</span>
          )}
          {camp.almost_sold_out && !camp.is_sold_out && (
            <span style={badgeStyle('#FEF3C7', '#92400E')}>Almost Full</span>
          )}
          {camp.registration_ending && !camp.is_sold_out && (
            <span style={badgeStyle('#FEF3C7', '#92400E')}>Reg. Ending</span>
          )}
          {camp.wait_list && (
            <span style={badgeStyle('#E0E7FF', '#3730A3')}>Waitlist</span>
          )}
          {camp.ryzer_verified && (
            <span style={badgeStyle('#D1FAE5', '#065F46')}>Verified</span>
          )}
        </div>

        {/* Register button */}
        {camp.registration_url && !camp.is_sold_out && (
          <button
            onClick={() => onRegister(camp.registration_url)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '6px 14px',
              background: '#F9550D',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
              whiteSpace: 'nowrap',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#E04A0B'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#F9550D'; }}
          >
            Register <ExternalLink size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ----------- Shared Styles ----------- */

/* Passed to <Select>, which is width:100% by default — these four are a
 * content-sized filter row, hence width:auto, and the right padding is what
 * clears the primitive's chevron. */
const selectStyle: React.CSSProperties = {
  width: 'auto',
  padding: '7px 30px 7px 10px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '500',
  minWidth: '120px'
};

const tagStyle = (bg: string, color: string): React.CSSProperties => ({
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: '600',
  background: bg,
  color: color,
  whiteSpace: 'nowrap'
});

const badgeStyle = (bg: string, color: string): React.CSSProperties => ({
  padding: '2px 7px',
  borderRadius: '10px',
  fontSize: '10px',
  fontWeight: '700',
  background: bg,
  color: color,
  whiteSpace: 'nowrap'
});

const paginationBtnStyle = (disabled: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  background: disabled ? '#f9fafb' : '#fff',
  color: disabled ? '#d1d5db' : '#374151',
  fontSize: '13px',
  fontWeight: '600',
  cursor: disabled ? 'default' : 'pointer',
  fontFamily: 'Manrope, sans-serif',
  opacity: disabled ? 0.5 : 1
});

export default CampsModal;
